import { Gamepad2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getArcadePlayers } from '@/lib/arcade-controls';
import { DEFAULT_ARCADE_MAPPING } from '@/lib/constants';

interface ArcadePhysicalKeyMapFieldProps {
  value: Record<string, string> | null | undefined;
  onChange: (value: Record<string, string> | null) => void;
  disabled?: boolean;
}

const PLAYERS = getArcadePlayers('two_player');

export function ArcadePhysicalKeyMapField({
  value,
  onChange,
  disabled,
}: ArcadePhysicalKeyMapFieldProps) {
  const enabled = value != null;
  const mapping = value ?? DEFAULT_ARCADE_MAPPING;

  const handleToggle = (checked: boolean) => {
    onChange(checked ? { ...DEFAULT_ARCADE_MAPPING } : null);
  };

  const handleInputChange = (code: string, next: string) => {
    onChange({ ...mapping, [code]: next.toLowerCase().trim() });
  };

  const handleReset = () => {
    onChange({ ...DEFAULT_ARCADE_MAPPING });
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div>
        <h3 className="font-semibold text-sm">Arcade cabinet key mapping</h3>
        <p className="text-muted-foreground text-xs">
          Maps each logical control to the keyboard key the physical USB encoder
          emits. Leave disabled to use the default wiring. Enable to remap the
          whole cabinet in software without rewiring — every game in this event
          picks up the change.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="arcade-physical-key-map-toggle"
          checked={enabled}
          onCheckedChange={(checked) => handleToggle(checked === true)}
          disabled={disabled}
        />
        <Label
          htmlFor="arcade-physical-key-map-toggle"
          className="cursor-pointer text-sm"
        >
          Override cabinet key mapping
        </Label>
      </div>

      {enabled && (
        <div className="space-y-4 pt-1">
          {PLAYERS.map((player) => (
            <div key={player.id} className="space-y-2">
              <div className="flex items-center gap-2">
                <Gamepad2 className="h-4 w-4" />
                <h4 className="font-semibold text-sm">{player.title}</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {player.controls.map((control) => (
                  <div key={control.code} className="space-y-1.5">
                    <Label htmlFor={control.code} className="text-xs">
                      {control.label}
                    </Label>
                    <Input
                      id={control.code}
                      value={mapping[control.code] ?? ''}
                      onChange={(e) =>
                        handleInputChange(control.code, e.target.value)
                      }
                      placeholder={DEFAULT_ARCADE_MAPPING[control.code] ?? ''}
                      className="h-8 font-mono text-sm"
                      disabled={disabled}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={handleReset}
            disabled={disabled}
            className="text-muted-foreground text-xs underline hover:text-foreground"
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
}
